import React from "react";
import { Plus } from "lucide-react";
import { Avatar } from "./avatar";

interface SortAttribute {
  label: string;
  value?: string;
}

interface ProfileCardProps {
  entity: any;
  entityType: string;
  sortAttributes?: SortAttribute[];
  onClick?: () => void;
  onDetailClick?: () => void;
  selected?: boolean;
  disabled?: boolean;
}

export function ProfileCard({
  entity,
  entityType,
  sortAttributes = [],
  onClick,
  onDetailClick,
  selected = false,
  disabled = false
}: ProfileCardProps) {
  const getAvatarUrl = () => {
    if (entityType === "candidats") return entity.avatar_url;
    return entity.logo_url;
  };

  const getEntityType = (): "candidat" | "enseigne" | "ville" | "location" => {
    if (entityType === "candidats") return "candidat";
    if (entityType === "enseignes") return "enseigne";
    if (entityType === "villes") return "ville";
    return "location";
  };

  const getSubtitle = () => {
    // If sortAttributes provided, use them to build subtitle dynamically
    if (sortAttributes && sortAttributes.length > 0) {
      const values: string[] = [];

      sortAttributes.forEach((attr) => {
        const value = getEntityAttributeValue(attr.label);
        if (value) values.push(value);
      });

      return values.join(" • ");
    }

    // Fallback to hardcoded logic if no sortAttributes
    switch (entityType) {
      case "candidats":
        return [
          entity.project_contribution
            ? `${Math.round(entity.project_contribution / 1000)}k€`
            : null,
          entity.project_sectors && entity.project_sectors.length > 0
            ? entity.project_sectors.length > 1
              ? `${entity.project_sectors[0]} (+${entity.project_sectors.length - 1})`
              : entity.project_sectors[0]
            : null,
          entity.project_address,
        ]
          .filter(Boolean)
          .join(" • ");
      case "enseignes":
        return [
          entity.chalandise ? `${entity.chalandise}hab.` : null,
          entity.surface_display,
          entity.contribution
            ? `${Math.round(entity.contribution / 1000)}k€`
            : null,
        ]
          .filter(Boolean)
          .join(" - ");
      case "villes":
        return [
          entity.chalandise ? `${entity.chalandise} hab.` : null,
          entity.sectors?.length
            ? `${entity.sectors.length} secteur${entity.sectors.length > 1 ? "s" : ""}`
            : null,
        ]
          .filter(Boolean)
          .join(" • ");
      case "locations":
        return [entity.address, entity.surface ? `${entity.surface}m²` : null]
          .filter(Boolean)
          .join(" - ");
      default:
        return "";
    }
  };

  const getEntityAttributeValue = (attributeLabel: string): string | null => {
    switch (attributeLabel) {
      case "Géolocalisation":
        return entity.address || entity.project_address || null;
      case "Secteurs recherchés":
      case "Secteurs prioritaires":
      case "Secteur":
        if (entity.sector) return entity.sector;
        // Check both sectors (for villes) and project_sectors (for candidats)
        const sectors = entity.sectors || entity.project_sectors;
        if (sectors && sectors.length > 0) {
          return sectors.length > 1
            ? `${sectors[0]} (+${sectors.length - 1})`
            : sectors[0];
        }
        return null;
      case "Apport":
        if (entity.contribution) {
          return `${Math.round(entity.contribution / 1000)}k€`;
        }
        if (entity.project_contribution) {
          return `${Math.round(entity.project_contribution / 1000)}k€`;
        }
        return null;
      case "Zone de chalandise":
        if (entity.chalandise) {
          return `${entity.chalandise.toLocaleString()} hab.`;
        }
        return null;
      case "Surface":
        if (entity.surface) {
          return `${entity.surface}m²`;
        }
        if (entity.surface_min && entity.surface_max) {
          return `${entity.surface_min}-${entity.surface_max}m²`;
        }
        if (entity.surface_min) {
          return `min ${entity.surface_min}m²`;
        }
        if (entity.surface_max) {
          return `max ${entity.surface_max}m²`;
        }
        return null;
      default:
        return null;
    }
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleDetailClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDetailClick) {
      onDetailClick();
    }
  };

  const cardClasses = [
    "card p-2 !shadow-none transition-all",
    selected && "bg-default-50 ring-2 ring-default-200",
    disabled && "opacity-40"
  ].filter(Boolean).join(" ");

  return (
    <div className={cardClasses}>
      <div className="cursor-pointer" onClick={handleCardClick}>
        <div className="flex items-center justify-between space-x-2">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Avatar
              entityType={getEntityType()}
              imageUrl={getAvatarUrl()}
              name={entity.name}
              size="md"
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-900 truncate text-sm">
                {entity.name}
              </div>
              <div className="text-xs text-slate-600 truncate">
                {getSubtitle()}
              </div>
            </div>
          </div>
          <button
            onClick={handleDetailClick}
            className="w-8 h-8 aspect-square rounded-md bg-white hover:bg-slate-50 border border-slate-200 shadow-sm inline-flex items-center justify-center flex-shrink-0 group"
          >
            <Plus
              className="text-slate-400 group-hover:text-slate-600"
              size={16}
            />
          </button>
        </div>
      </div>
    </div>
  );
}
